import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod/v3';
import { profileApi } from '@/api/admin/profiles';
import { userApi } from '@/api/admin/users';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { FormSkeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import { maskCpf } from '@/lib/formatters';

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  username: z.string().min(1, 'Usuário é obrigatório'),
  email: z.string().email('Email inválido'),
  cpf: z.string().min(11, 'CPF inválido'),
  profileId: z.string().min(1, 'Selecione um perfil'),
  // biome-ignore lint/suspicious/noExplicitAny: FileList from input[type=file] has no Zod equivalent
  image: z.any().optional(),
});

type FormData = z.infer<typeof schema>;

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Dialog states
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [activeDialogOpen, setActiveDialogOpen] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');

  // Avatar URL state (fetched asynchronously via presigned URL)
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userApi.findOne(id!),
    enabled: !!id,
  });

  const { data: profilesData, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => profileApi.findMany({ limit: 100 }),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      username: '',
      email: '',
      cpf: '',
      profileId: '',
      image: undefined,
    },
  });

  const profileIdValue = form.watch('profileId');
  const selectedProfile = useMemo(
    () => profilesData?.docs?.find((p) => p.id === profileIdValue) ?? null,
    [profilesData?.docs, profileIdValue],
  );

  // Populate form when user data is loaded
  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        username: user.username,
        email: user.email,
        cpf: maskCpf(user.cpf),
        profileId: user.profileId ?? '',
        image: undefined,
      });
    }
  }, [user, form]);

  // Fetch avatar URL asynchronously (presigned download URL)
  useEffect(() => {
    if (user?.image?.key && id) {
      userApi
        .getAvatarUrl(id, user.image.key)
        .then(setAvatarUrl)
        .catch(() => setAvatarUrl(undefined));
    } else {
      setAvatarUrl(undefined);
    }
  }, [user?.image?.key, id]);

  // Update mutation - uses presigned URL flow for avatar upload
  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      // Step 1: Handle avatar upload via presigned URL if there's an image
      if (data.image && data.image.length > 0) {
        const file = data.image[0] as File;

        // Get presigned upload URL from backend
        const { uploadUrl, key } = await userApi.prepareAvatarUpload({
          userId: id!,
          fileName: file.name,
          contentType: file.type,
        });

        // Upload directly to S3 using presigned URL
        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!s3Response.ok) {
          throw new Error('Falha ao enviar imagem para S3');
        }

        // Confirm upload with backend (saves metadata to MongoDB)
        await userApi.confirmAvatarUpload({
          userId: id!,
          key,
        });
      }

      // Step 2: Update user data
      return userApi.update(id!, {
        name: data.name,
        username: data.username,
        email: data.email,
        cpf: data.cpf,
        profileId: data.profileId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast({
        title: 'Sucesso',
        description: 'Usuário atualizado com sucesso.',
        variant: 'success',
      });
      navigate('/admin/users');
    },
  });

  // Lock/Unlock mutation
  const lockMutation = useMutation({
    mutationFn: ({ lock }: { lock: boolean }) => userApi.lock(id!, lock),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      setLockDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Erro ao alterar bloqueio',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  // Activate/Deactivate mutation
  const activeMutation = useMutation({
    mutationFn: ({ active }: { active: boolean }) => userApi.setActive(id!, active),
    onSuccess: (_result, { active }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      setActiveDialogOpen(false);
      toast({
        variant: 'success',
        title: 'Sucesso',
        description: active ? 'Usuário ativado com sucesso.' : 'Usuário inativado com sucesso.',
      });
    },
    onError: (error, { active }) => {
      toast({
        title: active ? 'Erro ao ativar usuário' : 'Erro ao inativar usuário',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (sendToEmail?: string) => userApi.resetPassword(id!, sendToEmail),
    onSuccess: (_result, sendToEmail) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      setResetPasswordDialogOpen(false);
      toast({
        variant: 'success',
        title: 'Sucesso',
        description: `Email de redefinição enviado para ${sendToEmail || user?.email}.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao redefinir senha',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(data);
  };

  // User state checks
  const isLocked = user?.lock ?? false;
  const isActive = user?.active ?? true;

  if (isLoadingUser || isLoadingProfiles) {
    return (
      <div className="w-full">
        <Card>
          <CardContent className="pt-6">
            <FormSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Usuário não encontrado</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Voltar"
          onClick={() => navigate('/admin/users')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Atualização de usuário - {user.name}
          </h1>
          <p className="text-sm text-gray-500">
            Preencha o formulário abaixo para atualizar um usuário.
          </p>
        </div>
      </div>

      {/* Form Card */}
      <Card className="w-full">
        <CardContent className="pt-6">
          <Form {...form}>
            <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Action Buttons at TOP */}
              <div className="flex gap-2 justify-end border-b pb-6">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setLockDialogOpen(true)}
                  disabled={!isActive}
                >
                  {isLocked ? 'Desbloquear Usuário' : 'Bloquear Usuário'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setResetPasswordDialogOpen(true)}
                  disabled={isLocked || !isActive}
                >
                  Reset de Senha
                </Button>
                <Button type="button" variant="secondary" onClick={() => setActiveDialogOpen(true)}>
                  {isActive ? 'Inativar' : 'Ativar'}
                </Button>
              </div>

              {/* Profile Selection */}
              <FormField
                control={form.control}
                name="profileId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Perfil</FormLabel>
                    <FormControl>
                      <Combobox
                        data={profilesData?.docs ?? []}
                        keyExtractor={(profile) => profile.id}
                        displayValueGetter={(profile) => profile.name}
                        type="single"
                        value={selectedProfile ? [selectedProfile] : []}
                        onOptionChange={(selected) => {
                          field.onChange(selected[0]?.id ?? '');
                        }}
                        placeholder="Selecione o perfil"
                        isLoading={isLoadingProfiles}
                        disabled={!isActive}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="000.000.000-00"
                          {...field}
                          onChange={(e) => {
                            field.onChange(maskCpf(e.target.value));
                          }}
                          disabled={!isActive}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome completo</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Digite o nome completo"
                          {...field}
                          disabled={!isActive}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Digite o email"
                          {...field}
                          disabled={!isActive}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Usuário</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Digite o nome de usuário"
                          {...field}
                          disabled={!isActive}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Profile Picture Upload */}
              <Controller
                control={form.control}
                name="image"
                render={({ field }) => (
                  <FileUpload
                    disabled={!isActive}
                    label="Imagem de perfil"
                    handleFile={field.onChange}
                    multiple={false}
                    accept="jpeg, jpg, png"
                    currentImageUrl={avatarUrl}
                  />
                )}
              />

              <FormError error={updateMutation.error} onDismiss={updateMutation.reset} />

              <div className="flex gap-3 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={updateMutation.isPending || !isActive}>
                  {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Lock/Unlock Dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isLocked ? 'Desbloquear Usuário' : 'Bloquear Usuário'}</DialogTitle>
            <DialogDescription>
              {isLocked
                ? `Tem certeza que deseja desbloquear o usuário "${user.name}"?`
                : `Tem certeza que deseja bloquear o usuário "${user.name}"? O usuário não poderá acessar o sistema.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant={isLocked ? 'default' : 'destructive'}
              onClick={() => lockMutation.mutate({ lock: !isLocked })}
              disabled={lockMutation.isPending}
            >
              {lockMutation.isPending ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onOpenChange={(open) => {
          setResetPasswordDialogOpen(open);
          if (open) setResetPasswordEmail(user.email);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>
              Um email será enviado com um link para redefinição de senha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label htmlFor="resetPasswordEmail" className="text-sm font-medium">
              Email de envio
            </label>
            <Input
              id="resetPasswordEmail"
              type="email"
              value={resetPasswordEmail}
              onChange={(e) => setResetPasswordEmail(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => resetPasswordMutation.mutate(resetPasswordEmail)}
              disabled={
                resetPasswordMutation.isPending ||
                !resetPasswordEmail.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetPasswordEmail)
              }
            >
              {resetPasswordMutation.isPending ? 'Enviando...' : 'Enviar email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate/Deactivate Dialog */}
      <Dialog open={activeDialogOpen} onOpenChange={setActiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isActive ? 'Inativar Usuário' : 'Ativar Usuário'}</DialogTitle>
            <DialogDescription>
              {isActive
                ? `Tem certeza que deseja inativar o usuário "${user.name}"?`
                : `Tem certeza que deseja ativar o usuário "${user.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant={isActive ? 'destructive' : 'default'}
              onClick={() => activeMutation.mutate({ active: !isActive })}
              disabled={activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
